import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	Info,
	type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/util/string";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";

export type InfoTooltipSeverity = "info" | "warning" | "error" | "success";

interface InfoTooltipProps {
	content: ReactNode;
	severity?: InfoTooltipSeverity;
	className?: string;
	triggerClassName?: string;
}

const severityConfig: Record<
	InfoTooltipSeverity,
	{ icon: LucideIcon; className: string }
> = {
	info: {
		icon: Info,
		className: "text-base-content/50 hover:text-base-content",
	},
	warning: {
		icon: AlertTriangle,
		className: "text-amber-500 hover:text-amber-600",
	},
	error: {
		icon: AlertCircle,
		className: "text-error hover:text-error/80",
	},
	success: {
		icon: CheckCircle,
		className: "text-emerald-500 hover:text-emerald-600",
	},
};

export function InfoTooltip({
	content,
	severity = "info",
	className,
	triggerClassName,
}: InfoTooltipProps) {
	if (!content) return null;

	const { icon: Icon, className: severityClassName } = severityConfig[severity];

	return (
		<Tooltip>
			<TooltipTrigger
				className={cn(
					"transition-colors cursor-pointer",
					severityClassName,
					triggerClassName,
				)}
			>
				<Icon className="size-4" />
			</TooltipTrigger>
			<TooltipContent className={cn("max-w-xs", className)}>
				{content}
			</TooltipContent>
		</Tooltip>
	);
}
